import { createContext, useContext, useState, useCallback, lazy, Suspense } from "react";

// Lazy — StatusDialog pulls in lottie-react (+ 2 animation JSONs), which is
// sizable. NotificationProvider wraps the whole app non-lazily, so a static
// import here would ship that weight in main.js on every page load (Login
// included) even though nothing has been notified yet. `everOpened` delays
// the import() itself until the first showNotification call, not just its
// render — closing the dialog afterwards keeps it mounted (normal Dialog
// open/close), so the chunk only ever loads once.
const StatusDialog = lazy(() => import("../Component/StatusDialog"));

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [dialog, setDialog] = useState({ open: false, status: "", message: "" });
  const [everOpened, setEverOpened] = useState(false);

  const showNotification = useCallback((status, message) => {
    setEverOpened(true);
    setDialog({ open: true, status, message });
  }, []);

  const handleClose = () => setDialog((d) => ({ ...d, open: false }));

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      {everOpened && (
        <Suspense fallback={null}>
          <StatusDialog
            open={dialog.open}
            status={dialog.status}
            message={dialog.message}
            onClose={handleClose}
          />
        </Suspense>
      )}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be used inside NotificationProvider");
  return ctx;
}
